import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { subPaymentsApi, subCommitmentsApi, subFinancialsApi, projectsApi, subcontractorsApi } from '../api';
import { dollarsToCents, centsToDollarString, centsToDollarInput, fmtDate } from '../constants';
import { MoneyInput } from './MoneyInput';
import { SUB_PAY_METHOD_OPTIONS, subPayMethodLabel } from '../subConstants';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

const Row = ({ l, v }) => (
  <div className="flex justify-between gap-3 py-1 text-sm">
    <span className="text-gray-500">{l}</span>
    <span className="text-gray-200 text-right">{v}</span>
  </div>
);

export const SubPaymentFormDialog = ({ open, onOpenChange, payment, lockedProjectId, lockedSubcontractorId, onSaved }) => {
  const isNew = !payment;
  const voided = payment?.status === 'voided';
  const [projects, setProjects] = useState([]);
  const [subs, setSubs] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ project_id: '', subcontractor_id: '', commitment_id: '', payment_date: '', amount: '', payment_method: '', reference_number: '', internal_notes: '' });

  useEffect(() => {
    if (!open) return;
    if (isNew) {
      projectsApi.list({ sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
      subcontractorsApi.list({}).then((r) => setSubs(r.data)).catch(() => {});
    }
    if (payment) {
      setF({
        project_id: payment.project_id, subcontractor_id: payment.subcontractor_id,
        commitment_id: payment.commitment_id || '', payment_date: payment.payment_date,
        amount: centsToDollarInput(payment.amount_cents), payment_method: payment.payment_method || '',
        reference_number: payment.reference_number || '', internal_notes: payment.internal_notes || '',
      });
    } else {
      setF({ project_id: lockedProjectId || '', subcontractor_id: lockedSubcontractorId || '', commitment_id: '', payment_date: new Date().toISOString().slice(0, 10), amount: '', payment_method: '', reference_number: '', internal_notes: '' });
    }
  }, [open, payment, isNew, lockedProjectId, lockedSubcontractorId]);

  // Commitment options + live balance preview for chosen project + subcontractor
  useEffect(() => {
    if (!open || voided || !f.project_id || !f.subcontractor_id) { setCommitments([]); setBalance(null); return; }
    subCommitmentsApi.list({ project_id: f.project_id, subcontractor_id: f.subcontractor_id })
      .then((r) => setCommitments(r.data))
      .catch(() => {});
    subFinancialsApi.summary({ project_id: f.project_id, subcontractor_id: f.subcontractor_id })
      .then((r) => setBalance(r.data))
      .catch(() => {});
  }, [open, voided, f.project_id, f.subcontractor_id]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v, ...((k === 'project_id' || k === 'subcontractor_id') ? { commitment_id: '' } : {}) }));

  const amountCents = dollarsToCents(f.amount);
  const overpay = balance && amountCents != null && balance.known_balance_cents > 0 && amountCents > balance.known_balance_cents;

  const save = async () => {
    if (busy) return;
    if (!f.project_id) { toast.error('Select a project'); return; }
    if (!f.subcontractor_id) { toast.error('Select a subcontractor'); return; }
    if (!f.payment_date) { toast.error('Payment date is required'); return; }
    if (amountCents == null || amountCents <= 0) { toast.error('Payment amount must be greater than $0'); return; }
    setBusy(true);
    try {
      const body = {
        commitment_id: f.commitment_id || null, payment_date: f.payment_date,
        amount_cents: amountCents, payment_method: f.payment_method || null,
        reference_number: f.reference_number || null, internal_notes: f.internal_notes || null,
      };
      const res = isNew
        ? await subPaymentsApi.create({ ...body, project_id: f.project_id, subcontractor_id: f.subcontractor_id })
        : await subPaymentsApi.update(payment.id, body);
      toast.success(isNew ? 'Payment recorded' : 'Payment updated');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not save payment');
    } finally { setBusy(false); }
  };

  const action = async (fn, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Action failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sub-payment-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Record Subcontractor Payment' : voided ? 'Voided Payment' : 'Edit Subcontractor Payment'}</DialogTitle>
          <DialogDescription className="text-gray-500">
            Payments reduce the known subcontractor balance. They never become project costs.
          </DialogDescription>
        </DialogHeader>

        {voided ? (
          <div className="space-y-1 py-1" data-testid="sp-readonly">
            <Row l="Subcontractor" v={payment.subcontractor?.display_name} />
            <Row l="Project" v={payment.project?.name} />
            {payment.commitment && <Row l="Commitment" v={payment.commitment.title} />}
            <Row l="Date" v={fmtDate(payment.payment_date)} />
            <Row l="Amount" v={centsToDollarString(payment.amount_cents)} />
            {payment.payment_method && <Row l="Method" v={subPayMethodLabel(payment.payment_method)} />}
            {payment.reference_number && <Row l="Reference" v={payment.reference_number} />}
            {payment.void_reason && <Row l="Void Reason" v={payment.void_reason} />}
            {payment.voided_at && <Row l="Voided" v={`${fmtDate(payment.voided_at.slice(0, 10))} by ${payment.voided_by_username}`} />}
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Project *</label>
                {isNew ? (
                  <select value={f.project_id} onChange={(e) => set('project_id', e.target.value)} className={selectCls} disabled={Boolean(lockedProjectId)} data-testid="sp-project">
                    <option value="">Select project…</option>
                    {lockedProjectId && !projects.some((p) => p.id === lockedProjectId) && <option value={lockedProjectId}>Current Project</option>}
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_archived ? ' (Archived)' : ''}</option>)}
                  </select>
                ) : (
                  <div className="text-sm text-gray-200 bg-[#1A1A1A] border border-gray-800 rounded-md px-3 py-2">{payment.project?.name}</div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Subcontractor *</label>
                {isNew ? (
                  <select value={f.subcontractor_id} onChange={(e) => set('subcontractor_id', e.target.value)} className={selectCls} disabled={Boolean(lockedSubcontractorId)} data-testid="sp-subcontractor">
                    <option value="">Select subcontractor…</option>
                    {lockedSubcontractorId && !subs.some((s) => s.id === lockedSubcontractorId) && <option value={lockedSubcontractorId}>Current Subcontractor</option>}
                    {subs.map((s) => <option key={s.id} value={s.id}>{s.display_name}{s.is_archived ? ' (Archived)' : s.status !== 'active' ? ' (Inactive)' : ''}</option>)}
                  </select>
                ) : (
                  <div className="text-sm text-gray-200 bg-[#1A1A1A] border border-gray-800 rounded-md px-3 py-2">{payment.subcontractor?.display_name}</div>
                )}
              </div>
            </div>

            {balance && (
              <div className="bg-[#1A1A1A] border border-gray-800 rounded-md p-3" data-testid="sp-balance-preview">
                <Row l="Total Counted Commitments" v={centsToDollarString(balance.total_counted_commitment_cents)} />
                <Row l="Recorded Payments" v={centsToDollarString(balance.recorded_payments_cents)} />
                <Row l="Current Known Balance" v={centsToDollarString(balance.known_balance_cents)} />
                {balance.known_balance_cents < 0 && (
                  <p className="text-xs text-amber-400/90 mt-1">This subcontractor currently has an advance / overpayment on this project.</p>
                )}
              </div>
            )}

            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Commitment (optional)</label>
              <select value={f.commitment_id} onChange={(e) => set('commitment_id', e.target.value)} className={selectCls} data-testid="sp-commitment">
                <option value="">Unallocated (general / deposit / lump-sum)</option>
                {commitments.map((c) => <option key={c.id} value={c.id}>{c.title} — {centsToDollarString(c.amount_cents)}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Payment Date *</label>
                <Input type="date" value={f.payment_date} onChange={(e) => set('payment_date', e.target.value)} className={inputCls} data-testid="sp-date" />
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Amount ($) *</label>
                <MoneyInput value={f.amount} onChange={(v) => set('amount', v)} className={inputCls} data-testid="sp-amount" />
              </div>
            </div>
            {overpay && (
              <p className="flex items-center gap-2 text-xs text-amber-400/90" data-testid="sp-overpay-warning">
                <AlertTriangle size={13} className="shrink-0" /> This payment exceeds the current known balance. It will create an advance / overpayment. You can still record it.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Method</label>
                <select value={f.payment_method} onChange={(e) => set('payment_method', e.target.value)} className={selectCls} data-testid="sp-method">
                  <option value="">Not specified</option>
                  {SUB_PAY_METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-300 mb-1.5 block font-medium">Reference #</label>
                <Input value={f.reference_number} onChange={(e) => set('reference_number', e.target.value)} placeholder="Check #, transfer ID…" className={inputCls} data-testid="sp-reference" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Internal Notes</label>
              <Input value={f.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} className={inputCls} data-testid="sp-notes" />
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-800">
          {voided ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-700 text-gray-300">Close</Button>
              <Button onClick={() => action(() => subPaymentsApi.restore(payment.id), 'Payment restored')} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="sp-restore">Restore Payment</Button>
            </>
          ) : (
            <>
              {!isNew && (
                <Button variant="outline" onClick={() => action(() => subPaymentsApi.void(payment.id), 'Payment voided')} disabled={busy} className="border-red-900 text-red-400 mr-auto" data-testid="sp-void">Void</Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-700 text-gray-300">Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="sp-save">
                {busy && <Loader2 size={14} className="animate-spin" />} {isNew ? 'Record Payment' : 'Save Changes'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
